// Localized country and language names.
//
// The portal ships English names for 249 countries (constant/country.js) and for
// the ISO 639-1 language list (constant/language.js). Hand-translating those two
// tables into every supported UI language would be thousands of strings to keep
// in sync, so the browser's own CLDR data is used instead via Intl.DisplayNames.
// Whatever the browser cannot resolve keeps the existing English label, which is
// the same degradation the portal had before.

// Legacy/non-standard codes used by the i18n resources and the manual metadata,
// mapped to the BCP 47 tags Intl understands.
const BCP47_ALIASES = {
  jp: 'ja',
  zhchs: 'zh-Hans',
  zhcht: 'zh-Hant',
  'zh-chs': 'zh-Hans',
  'zh-cht': 'zh-Hant',
  'zh-ans': 'zh-Hans',
  'zh-ant': 'zh-Hant',
};

export const toBcp47 = (code) => BCP47_ALIASES[code] || code || 'en';

const supportsDisplayNames =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function';

// Intl.DisplayNames construction is not free, and the country dropdown resolves
// 249 entries on every language change, so keep one instance per (locale, type).
const cache = new Map();

const getFormatter = (locale, type) => {
  const key = `${locale}:${type}`;
  if (cache.has(key)) {
    return cache.get(key);
  }
  let formatter = null;
  if (supportsDisplayNames) {
    try {
      // `fallback: 'none'` makes an unknown code return undefined instead of
      // echoing the code back, so the caller can use its English label.
      formatter = new Intl.DisplayNames([locale], { type, fallback: 'none' });
    } catch (error) {
      formatter = null;
    }
  }
  cache.set(key, formatter);
  return formatter;
};

const resolve = (type, code, uiLanguage, fallbackLabel) => {
  if (!code) {
    return fallbackLabel || '';
  }
  const formatter = getFormatter(toBcp47(uiLanguage), type);
  if (formatter) {
    try {
      const name = formatter.of(type === 'region' ? String(code).toUpperCase() : toBcp47(code));
      if (name) {
        return name;
      }
    } catch (error) {
      // Malformed code — fall through to the English label.
    }
  }
  return fallbackLabel || String(code).toUpperCase();
};

// ISO 3166-1 alpha-2 country code -> name in the current UI language.
export const getCountryLabel = (code, uiLanguage, fallbackLabel) =>
  resolve('region', code, uiLanguage, fallbackLabel);

// Language code -> name in the current UI language.
export const getLanguageLabel = (code, uiLanguage, fallbackLabel) =>
  resolve('language', code, uiLanguage, fallbackLabel);
